import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import {
  createLinkTrackingVisit,
  getCustomerAddresses,
  getLoyaltyEnrollmentByCode,
  getVisitorToken,
  logClientError,
} from "../../utils/APICalls/APICalls";
import { getCookie, setCookie } from "../../utils/CookieHelper";
import { AlertErrorType } from "../../utils/ErrorTypes";
import LS from "../../utils/LocalStorage";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  DeliveryTypeDisplayName,
  LocalStorageEnum,
  OrderTypesEnum,
} from "../../utils/enums";
import envConfig from "../../utils/env.config";
import { refreshSocketAuth } from "../../utils/socket";
import * as userHelper from "../../utils/help/user/user";
import { Address } from "../../utils/types";
import { getOrganizationCustomerData } from "../redux/http/getCustomerData";
import { getCustomerNotifications } from "../redux/http/getNotifications";
import { getReferralCode } from "../redux/http/getReferralCode";
import {
  setSavedAddresses,
  setSelectedAddress,
} from "../redux/slices/deliverySlice";
import { setAddressSelectionDrawerState } from "../redux/slices/globalComponentsToggleSlice";
import { setOrganizationCustomer } from "../redux/slices/partnerAccountSlice";
import { toggleVisited } from "../redux/slices/referenceSlice";
import {
  sendGetReferralCodeRequest,
  setCalledVisitorTokenRequest,
} from "../redux/slices/requestLimiterSlice";
import {
  setPhoneNumberInfo,
  setReferralCode,
  setToken,
} from "../redux/slices/userInfoSlice";
import { useCustomerAnalytics } from "./useCustomerAnalytics";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";
import { selectCurrentDeliveryConfig } from "../redux/selectors/deliverySelectors";

const useCustomerData = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const getReferralCodeRequests = useAppSelector(
    (state) => state.requestLimiter.getReferralCodeRequests
  );
  const delivery = useAppSelector((state) => state.delivery);
  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantConfig?.hasWelcomeDrawerV3
  );
  const { referenceName, linkVisited } = useAppSelector(
    (state) => state.reference
  );
  const [alertSent, setAlertSent] = useState(false);

  const token = useAppSelector((state) => state.userInfo.token);

  const addCustomerAnalytics = useCustomerAnalytics();

  useEffect(() => {
    const restaurantId =
      restaurantInfo?.id || parseInt(envConfig.restaurantId!);

    if (restaurantId && token && !userHelper.isVisitor()) {
      dispatch(getOrganizationCustomerData(restaurantId));
      dispatch(getReferralCode(restaurantId));
      dispatch(sendGetReferralCodeRequest());
      const now = new Date();
      if (
        !alertSent &&
        getReferralCodeRequests.count > 10 &&
        Math.abs(now.valueOf() - getReferralCodeRequests.lastCalledAt) /
          (1000 * 60) <
          1
      ) {
        const decodedToken = userHelper.parseJwt(token);
        logClientError(
          `${AlertErrorType.GetReferral_Called_More_than_10_times}`,
          {
            customerId: decodedToken?.customer_id,
            restaurantInfo,
          }
        );
        setAlertSent(true);
      }
    } else {
      dispatch(setOrganizationCustomer(undefined));
      dispatch(
        setReferralCode({
          promoCode: {
            name: "",
            packageOffer: undefined,
            countPerUse: 0,
            expired: false,
          },
          rewardedPoints: 0,
          remainingInvitations: 0,
        })
      );
    }
  }, [token, restaurantInfo?.id]);

  useEffect(() => {
    dispatch(setToken(getCookie(COOKIE_ENUM.TOKEN)));
  });

  useEffect(() => {
    if (!userHelper.isVisitor()) {
      getCustomerAddress();
    }
  }, [selectedFranchise?.id, token]);

  useEffect(() => {
    const restaurantId =
      restaurantInfo?.id || parseInt(envConfig.restaurantId!);
    if (!userHelper.isVisitor()) {
      dispatch(getCustomerNotifications({ restaurantId }));
    }
  }, [router.pathname]);

  useEffect(() => {
    if (
      router.isReady &&
      router.query.code &&
      router.query.code !== "undefined" &&
      userHelper.isVisitor() &&
      token
    ) {
      getLoyaltyEnrollmentByCode(router.query.code?.toString() as string).then(
        (res) => {
          if (res.data.phoneNumber) {
            addCustomerAnalytics(
              CUSTOMER_ANALYTICS_EVENT.LAND_WITH_ENROLLMENT,
              {
                phoneNumber: res.data.phoneNumber,
              }
            );
            LS._set(LocalStorageEnum.PHONE_NUMBER, res.data.phoneNumber);
            dispatch(
              setPhoneNumberInfo({
                phoneNumber: res.data.phoneNumber,
                countryCode:
                  res.data.countryCode || restaurantInfo?.preferredCountry,
              })
            );
          }
        }
      );
    }
  }, [token, router.isReady]);

  useEffect(() => {
    if (token) {
      if (
        typeof referenceName === "string" &&
        referenceName.length > 0 &&
        !linkVisited
      ) {
        createLinkTrackingVisit(referenceName);
        dispatch(toggleVisited(true));
      }
    }
  }, []);

  useEffect(() => {
    if (!getCookie(COOKIE_ENUM.TOKEN) && router.isReady) {
      dispatch(setCalledVisitorTokenRequest(true));
      getVisitorToken().then((res) => {
        const resToken = res.data.token;
        setCookie(COOKIE_ENUM.TOKEN, resToken);
        dispatch(setToken(resToken));
        refreshSocketAuth();
      });
    }
  }, [token, router.isReady]);

  const getCustomerAddress = useCallback(
    () =>
      getCustomerAddresses().then(
        (getCustomerAddressesRes: { data: Address[] }) => {
          // Only relevant under hasWelcomeDrawerV3: that's where a
          // deliveryV2 type other than table-delivery can exist with
          // usesMap incorrectly true in config data. Legacy restaurants
          // have no such ambiguity, so trust usesMap as-is there.
          const isTableDeliveryConfig = hasWelcomeDrawerV3
            ? currentDeliveryConfig?.deliveryType.displayName ===
              DeliveryTypeDisplayName.TABLE_DELIVERY
            : Boolean(currentDeliveryConfig?.usesMap);

          if (
            getCustomerAddressesRes?.data &&
            getCustomerAddressesRes.data.length > 0
          ) {
            if (
              !delivery?.selectedAddress &&
              orderTypeParamState === OrderTypesEnum.DELIVERYV2 &&
              isTableDeliveryConfig
            ) {
              dispatch(setAddressSelectionDrawerState(true));
            }
          }
          dispatch(setSavedAddresses(getCustomerAddressesRes.data));
          if (delivery.selectedAddress) {
            const address1Index = getCustomerAddressesRes.data.findIndex(
              (a) => a.id === delivery.selectedAddress?.id
            );
            if (address1Index > -1 && selectedFranchise) {
              // Nothing to do: this branch existed only to resolve the address's
              // zone and write its fee and estimated times into the store, and
              // both are derived from the selected address against the current
              // zones now.
            } else if (address1Index === -1) {
              // The persisted selectedAddress no longer appears in the fresh,
              // filtered address list (e.g. it lost its coordinates or was
              // converted to an offline/Foodics-synced address server-side).
              // Clear it instead of silently carrying a stale address id
              // through to checkout, and let the customer pick again.
              dispatch(setSelectedAddress(null));
              if (
                orderTypeParamState === OrderTypesEnum.DELIVERYV2 &&
                isTableDeliveryConfig
              ) {
                dispatch(setAddressSelectionDrawerState(true));
              }
            }
          }
        }
      ),
    [currentDeliveryConfig?.id, orderTypeParamState, selectedFranchise?.id]
  );
};

export default useCustomerData;
